import { Button } from 'antd';
import BreadCrumbs from 'common/breadcrumbs';
import UploadComponent from 'common/file-upload/components/upload-component';
import PageTitle from 'common/page-title';
import PageWrapper from 'common/page-wrapper';
import Table from 'common/table/table';
import React, { useState } from 'react';
import { HistoryOutlined } from '@ant-design/icons';
import {
  useExtractPCodesMutation,
  useGetPCodeOverviewQuery
} from 'services/pcodes/pcodes-api';
import { PCode } from 'services/pcodes/types';
import { useGetUserContextQuery } from 'services/user-context/user-context-api';
import getLocalTime from 'utils/get-local-time';
import PCodesDeltaHistoryModal from './pcodes-delta-history-modal';

// Regular expression to validate if the value is a valid email address
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const pcodesColumns = [
  {
    title: 'Code',
    dataIndex: 'code',
    width: 150,
    sorter: {
      compare: (a: PCode, b: PCode) => a.code > b.code
    }
  },
  {
    title: 'Name',
    dataIndex: 'name',
    sorter: {
      compare: (a: PCode, b: PCode) => a.name > b.name
    }
  },
  {
    title: 'Type',
    dataIndex: 'hyperionType',
    sorter: {
      compare: (a: PCode, b: PCode) => a.type > b.type
    }
  },
  {
    title: 'Status',
    dataIndex: 'status',
    sorter: {
      compare: (a: PCode, b: PCode) => a.status > b.status
    }
  },
  {
    title: 'Start Date',
    dataIndex: 'startDate',
    width: 100,
    render: (value: string) => getLocalTime(value, 'DD/MM/YYYY')
  },
  {
    title: 'End Date',
    dataIndex: 'endDate',
    width: 100,
    render: (value: string) => getLocalTime(value, 'DD/MM/YYYY')
  },
  {
    title: 'Project Owner / Process Architect',
    dataIndex: 'projectOwner',
    render: (value: string) => {
      if (value === '0') {
        return '';
      }
      
      // Check if value is a string and a valid email
      if (emailRegex.test(value)) {
        return <a href={`mailto:${value}`}>{value}</a>;
      }
  
      // Otherwise, return the value as-is
      return value;
    }
  },
  {
    title: 'Project (Business) Manager / Process Owner',
    dataIndex: 'projectManager',
    render: (value: string) => {
      if (value === '0') {
        return '';
      }
      
      // Check if value is a string and a valid email
      if (emailRegex.test(value)) {
        return <a href={`mailto:${value}`}>{value}</a>;
      }
  
      // Otherwise, return the value as-is
      return value;
    }
  },
  {
    title: 'Process / Product Leader',
    dataIndex: 'processLeader',
    render: (value: string) => {
      if (value === '0') {
        return '';
      }
      
      // Check if value is a string and a valid email
      if (emailRegex.test(value)) {
        return <a href={`mailto:${value}`}>{value}</a>;
      }
  
      // Otherwise, return the value as-is
      return value;
    }
  }
];

const Pcodes = () => {
  const [extractPCodes, { isLoading: isExtracting }] =
    useExtractPCodesMutation();
  const [historyIsOpen, setHistoryIsOpen] = useState(false);
  const { data: userContext } = useGetUserContextQuery();

  return (
    <PageWrapper backgroundColor="white">
      <div>
        {historyIsOpen && (
          <PCodesDeltaHistoryModal closeModal={() => setHistoryIsOpen(false)} />
        )}
        <BreadCrumbs />
        <PageTitle text="Pcodes" />

        <Table
          actionButtons={
            userContext?.roles.PCodeFull && [
              <Button
                icon={<HistoryOutlined />}
                onClick={() => {
                  setHistoryIsOpen(true);
                }}
              >
                Open Pcode history
              </Button>,

              <UploadComponent
                loading={isExtracting}
                uploadType="pcode-csv"
                allowedFileTypes={['.csv']}
                onFinish={(uniqueIdentifier: string) => {
                  extractPCodes(uniqueIdentifier);
                }}
              />
            ]
          }
          style={{ maxWidth: '1600px' }}
          query={useGetPCodeOverviewQuery}
          columns={pcodesColumns}
          endpoint=""
          rowKey="code"
          pageSize={7}
          filterOptions={{
            keys: ['code', 'name'],
            placeholder: 'Filter by code or name'
          }}
        />
      </div>
    </PageWrapper>
  );
};

export default Pcodes;
